import { PERMISSION_LEVEL } from "@constants/permissionLevel";
import Spinner from "@legacy/core/components/Spinner";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import Cookies from "js-cookie";
import pluralize from "pluralize";
import { Component } from "react";
import deepcopy from "rfdc";
import LineItemForm from "../core/forms/LineItemForm";
import { CustomIDGenerationModes, LineItemUnitTypes, PriceBookItemTypes, ShippingCarriers, ShippingMethods } from "../core/utils/enums";
import { currencyFormatter, getCurrencySymbol, historyHasState, sendDataToServer, valueIsDefined } from "../core/utils/utils";
import { validateLineItem } from "../invoices/utils/utils";
import VendorForm from "../pricebook/forms/VendorForm";
import PurchaseOrderForm from "./forms/PurchaseOrderForm";

dayjs.extend(timezone)

const FORM_MODES = {
    ADD_PURCHASE_ORDER: "ADD_PURCHASE_ORDER",
    EDIT_PURCHASE_ORDER: "EDIT_PURCHASE_ORDER",
    ADD_VENDOR: "ADD_VENDOR",
    EDIT_VENDOR: "EDIT_VENDOR",
    ADD_BILLING_VENDOR: "ADD_BILLING_VENDOR",
    EDIT_BILLING_VENDOR: "EDIT_BILLING_VENDOR",
    ADD_PART: "ADD_PART",
    EDIT_PART: "EDIT_PART",
}

const FORM_MODE_SUBTITLES = {
    ADD_PURCHASE_ORDER: "Add Purchase Order Details",
    EDIT_PURCHASE_ORDER: "Edit Purchase Order Details",
    ADD_VENDOR: "New Vendor",
    EDIT_VENDOR: "Edit Vendor",
    ADD_BILLING_VENDOR: "New Billing Vendor",
    EDIT_BILLING_VENDOR: "Edit Billing Vendor",
    ADD_PART: "Add Part or Material",
    EDIT_PART: "Edit Part or Material",
}

const FORM_MODE_BACK_BUTTON_DISPLAY = {
    ADD_PURCHASE_ORDER: "flex",
    EDIT_PURCHASE_ORDER: "flex",
    ADD_VENDOR: "none",
    EDIT_VENDOR: "none",
    ADD_BILLING_VENDOR: "none",
    EDIT_BILLING_VENDOR: "none",
    ADD_PART: "none",
    EDIT_PART: "none",
}

const PRIMARY_FORM_MODES = [FORM_MODES.ADD_PURCHASE_ORDER, FORM_MODES.EDIT_PURCHASE_ORDER]
const SECONDARY_FORM_MODES = [
    FORM_MODES.ADD_VENDOR,
    FORM_MODES.EDIT_VENDOR,
    FORM_MODES.ADD_BILLING_VENDOR,
    FORM_MODES.EDIT_BILLING_VENDOR,
    FORM_MODES.ADD_PART,
    FORM_MODES.EDIT_PART,
]

const VENDOR_FORM_MODES = [
    FORM_MODES.ADD_VENDOR,
    FORM_MODES.EDIT_VENDOR,
    FORM_MODES.ADD_BILLING_VENDOR,
    FORM_MODES.EDIT_BILLING_VENDOR,
]

const LINE_ITEM_FORM_MODES = [
    FORM_MODES.ADD_PART,
    FORM_MODES.EDIT_PART,
]

const FORM_DATA_NAMES_BY_MODE = {
    ADD_PURCHASE_ORDER: "purchaseOrderData",
    EDIT_PURCHASE_ORDER: "purchaseOrderData",
    ADD_VENDOR: "vendorData",
    EDIT_VENDOR: "vendorData",
    ADD_BILLING_VENDOR: "billingVendorData",
    EDIT_BILLING_VENDOR: "billingVendorData",
    ADD_PART: "lineItemData",
    EDIT_PART: "lineItemData",
}

const SUBMITTING_NAMES_BY_MODE = {
    ADD_PURCHASE_ORDER: "submittingPurchaseOrder",
    EDIT_PURCHASE_ORDER: "submittingPurchaseOrder",
    ADD_VENDOR: "submittingVendor",
    EDIT_VENDOR: "submittingVendor",
    ADD_BILLING_VENDOR: "submittingBillingVendor",
    EDIT_BILLING_VENDOR: "submittingBillingVendor",
    ADD_PART: "submittingLineItem",
    EDIT_PART: "submittingLineItem",
}

const ERROR_NAMES_BY_MODE = {
    ADD_PURCHASE_ORDER: "purchaseOrder",
    EDIT_PURCHASE_ORDER: "purchaseOrder",
    ADD_VENDOR: "vendor",
    EDIT_VENDOR: "vendor",
    ADD_BILLING_VENDOR: "billingVendor",
    EDIT_BILLING_VENDOR: "billingVendor",
    ADD_PART: "lineItem",
    EDIT_PART: "lineItem",
}

const LINE_ITEM_TYPES = {
    SERVICE_CHARGES: "service_charges",
    PARTS: "parts",
    OTHER_CHARGES: "other_charges",
    DISCOUNTS: "discounts",
}

const LINE_ITEM_TYPE_MAP = {
    [LINE_ITEM_TYPES.SERVICE_CHARGES]: PriceBookItemTypes.service,
    [LINE_ITEM_TYPES.PARTS]: PriceBookItemTypes.part,
    [LINE_ITEM_TYPES.OTHER_CHARGES]: PriceBookItemTypes.other,
    [LINE_ITEM_TYPES.DISCOUNTS]: PriceBookItemTypes.discount,
}

const LINE_ITEM_TYPE_BY_MODE = {
    ADD_PART: PriceBookItemTypes.part,
    EDIT_PART: PriceBookItemTypes.part,
}


class PurchaseOrderCreateContainer extends Component {

    // Initialize

    constructor(props) {
        super(props)

        const defaultMode = this.props.formMode || FORM_MODES.ADD_PURCHASE_ORDER
        this.addToastToQueue = this.props.addToastToQueue

        this.shippingCarrierChoices = window.SHIPPING_CARRIER_CHOICES
        this.shippingMethodChoices = window.SHIPPING_METHOD_CHOICES

        this.state = {
            purchaseOrderData: null,
            estimateData: null,
            vendorData: {},
            billingVendorData: {},
            lineItemData: {},
            deletedLineItemIDs: {},

            pendingSessionId: crypto.randomUUID(),
            attachments: [],
            isUploading: false,

            selectedVendor: null,
            selectedBillingVendor: null,
            showBillingVendorDropdown: false,
            selectedInventoryLocation: null,

            isDraft: false,

            errors: {
                purchaseOrder: {},
                vendor: {},
                billingVendor: {},
                lineItem: {},
            },

            defaultMode: defaultMode,
            mode: defaultMode,

            preferredTimezone: window.PREFERRED_TIMEZONE,
            currencyCode: window.CURRENCY_CODE,
            languageCode: window.LANGUAGE_CODE,
            showCustomPurchaseOrderIDField: window.PURCHASE_ORDER_CUSTOM_ID_GENERATION_MODE === CustomIDGenerationModes.manual,

            returnScroll: 0,
        }

        window.onpopstate = (event) => {
            if (event.state !== null && Object.keys(event.state).length) {
                this.setState(event.state)
            }
        }
    }

    componentDidMount = async () => {
        // If props tell us this is an edit view, grab purchase order data via rest
        if (this.state.defaultMode === FORM_MODES.EDIT_PURCHASE_ORDER && window.PURCHASE_ORDER_ID) {
            if (this.state.purchaseOrderData === null) {
                const purchaseOrderEndpoint = DjangoUrls["purchase-orders:api-purchase-orders-detail"](window.MARKETPLACE_ENTITY_SLUG, window.PURCHASE_ORDER_ID)
                const purchaseOrderResponse = await fetch(purchaseOrderEndpoint)
                const purchaseOrder = await purchaseOrderResponse.json()

                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.purchaseOrderData = purchaseOrder

                    updatedState.selectedVendor = purchaseOrder.vendor
                    updatedState.vendor_id = updatedState.selectedVendor.id
                    updatedState.selectedInventoryLocation = purchaseOrder.inventory_location
                    updatedState.inventory_location_id = updatedState.selectedInventoryLocation.id

                    updatedState.isDraft = purchaseOrder.is_draft

                    // Convert service company to slug
                    updatedState.purchaseOrderData.service_company = purchaseOrder.service_company.slug

                    // Convert vendor to ID
                    updatedState.purchaseOrderData.vendor = purchaseOrder.vendor.id

                    // Convert billing_vendor to ID (if exists)
                    if (purchaseOrder.billing_vendor) {
                        updatedState.selectedBillingVendor = purchaseOrder.billing_vendor
                        updatedState.purchaseOrderData.billing_vendor = purchaseOrder.billing_vendor.id
                        updatedState.showBillingVendorDropdown = true
                    }

                    // Convert inventory location to ID
                    updatedState.purchaseOrderData.inventory_location = purchaseOrder.inventory_location.id

                    // Convert estimate to ID (if exists)
                    if (purchaseOrder.estimate) {
                        updatedState.purchaseOrderData.estimate = purchaseOrder.estimate.id
                    }

                    updatedState.attachments = deepcopy()(purchaseOrder.attachments)
                    updatedState.labels = deepcopy()(purchaseOrder.labels)

                    this.resetpurchaseOrderData(updatedState, state, purchaseOrder)

                    updatedState.purchaseOrderData.service_charges = []
                    updatedState.purchaseOrderData.parts = purchaseOrder.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.part)
                    updatedState.purchaseOrderData.other_charges = []
                    updatedState.purchaseOrderData.discounts = []

                    return updatedState
                })

                // Re-fetch the source estimate so the Created From Estimate display and the
                // estimate line item dropdown have the full estimate to work with
                const hasEstimatesEntitlement = !!window.CURRENT_USER?.service_company?.entitlement_estimates_enabled
                const hasEstimatesPermissions = window.CURRENT_USER?.permissions.estimates_view_permission >= PERMISSION_LEVEL.FULL

                if (hasEstimatesEntitlement && hasEstimatesPermissions && purchaseOrder.estimate && this.state.estimateData === null) {
                    const estimateEndpoint = DjangoUrls["estimates:api-estimates-detail"](window.MARKETPLACE_ENTITY_SLUG, purchaseOrder.estimate.id)

                    const estimateResponse = await fetch(estimateEndpoint)
                    const estimate = await estimateResponse.json()

                    this.setState((state, props) => {
                        let updatedState = state
                        updatedState.estimateData = estimate
                        return updatedState
                    })
                }
            }
        }
        else {
            const localTime = dayjs.tz(undefined, window.PREFERRED_TIMEZONE)
            const localDateString = localTime.format("YYYY-MM-DD")

            // Check for from_estimate URL parameter
            const hasEstimatesEntitlement = !!window.CURRENT_USER?.service_company?.entitlement_estimates_enabled
            const hasEstimatesPermissions = window.CURRENT_USER?.permissions.estimates_view_permission >= PERMISSION_LEVEL.FULL
            const searchParams = new URLSearchParams(document.location.search)
            const fromEstimateID = searchParams.get("from_estimate") || null
            const vendorID = searchParams.get("vendor") || null
            const selectedLineItemIDsParameter = searchParams.get("line_items") || ""
            const selectedLineItemIDs = selectedLineItemIDsParameter
                .split(",")
                .map(lineItemID => parseInt(lineItemID))
                .filter(lineItemID => !isNaN(lineItemID))

            if (hasEstimatesEntitlement && hasEstimatesPermissions && fromEstimateID !== null && this.state.estimateData === null) {
                const estimateEndpoint = DjangoUrls["estimates:api-estimates-detail"](window.MARKETPLACE_ENTITY_SLUG, fromEstimateID)

                const estimateResponse = await fetch(estimateEndpoint)
                const estimate = await estimateResponse.json()

                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.estimateData = estimate
                    return updatedState
                })
            }
            else {
                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.estimateData = {}
                    return updatedState
                })
            }

            this.setState((state, props) => {
                let updatedState = state

                this.resetpurchaseOrderData(updatedState, state, {})

                updatedState.purchaseOrderData.service_company = window.MARKETPLACE_ENTITY_SLUG
                updatedState.purchaseOrderData.date_issued = localDateString

                updatedState.purchaseOrderData.service_charges = []
                updatedState.purchaseOrderData.parts = []
                updatedState.purchaseOrderData.other_charges = []
                updatedState.purchaseOrderData.discounts = []

                updatedState.purchaseOrderData.attachments = []
                updatedState.purchaseOrderData.labels = []

                // Pre-populate part line items from a source estimate's selected part lines
                // (if from an estimate). Reset each row's own id to null so it reads as "new",
                // pointing back to the source estimate line via estimate_line_item and seeding
                // the estimates M2M client-side. Non-part lines and unselected lines are skipped.
                if (Object.keys(state.estimateData).length !== 0) {
                    const estimatePartLineItems = (state.estimateData.line_items || []).filter(lineItem => (
                        lineItem.line_item_type === PriceBookItemTypes.part && selectedLineItemIDs.includes(lineItem.id)
                    ))
                    updatedState.purchaseOrderData.estimate = fromEstimateID
                    updatedState.purchaseOrderData.parts = estimatePartLineItems.map(lineItem => ({
                        id: null,
                        line_item_type: PriceBookItemTypes.part,
                        description: lineItem.description,
                        quantity: lineItem.quantity,
                        expected_cost: lineItem.cost,
                        estimate_line_item: lineItem.id,
                        estimates: [fromEstimateID],
                        platform_options: lineItem.platform_options,
                    }))
                }

                if (valueIsDefined(window.CURRENT_USER?.service_company?.default_purchase_order_preferred_shipping_carrier)) {
                    updatedState.purchaseOrderData.preferred_shipping_carrier = window.CURRENT_USER.service_company.default_purchase_order_preferred_shipping_carrier

                    if (
                        parseInt(updatedState.purchaseOrderData.preferred_shipping_carrier) === ShippingCarriers.other &&
                        window.CURRENT_USER.service_company.default_purchase_order_preferred_shipping_carrier_other_name
                    ) {
                        updatedState.purchaseOrderData.preferred_shipping_carrier_other_name = window.CURRENT_USER.service_company.default_purchase_order_preferred_shipping_carrier_other_name
                    }
                }
                if (valueIsDefined(window.CURRENT_USER?.service_company?.default_purchase_order_preferred_shipping_method)) {
                    updatedState.purchaseOrderData.preferred_shipping_method = window.CURRENT_USER.service_company.default_purchase_order_preferred_shipping_method

                    if (
                        parseInt(updatedState.purchaseOrderData.preferred_shipping_method) === ShippingMethods.other &&
                        window.CURRENT_USER.service_company.default_purchase_order_preferred_shipping_method_other_name
                    ) {
                        updatedState.purchaseOrderData.preferred_shipping_method_other_name = window.CURRENT_USER.service_company.default_purchase_order_preferred_shipping_method_other_name
                    }
                }
                return updatedState
            })

            // Preselect the vendor carried over from the source estimate's part selection.
            // Gated on the same permission the vendor select uses. Runs after the parts-prefill
            // setState so updateVendorSelection preserves the prefilled parts (it feeds the
            // current purchaseOrderData back through resetpurchaseOrderData). A full vendor object
            // is fetched because updateVendorSelection reads .id and .default_billing_vendor.
            const hasVendorsPermission = window.CURRENT_USER?.permissions.vendors_list_permission >= PERMISSION_LEVEL.RESTRICTED

            if (hasEstimatesEntitlement && hasEstimatesPermissions && fromEstimateID !== null && vendorID !== null && hasVendorsPermission) {
                const vendorEndpoint = DjangoUrls["pricebook:api-vendors-detail"](window.MARKETPLACE_ENTITY_SLUG, vendorID)

                try {
                    const vendorResponse = await fetch(vendorEndpoint)
                    if (vendorResponse.ok) {
                        const vendor = await vendorResponse.json()
                        this.updateVendorSelection(vendor)
                    }
                } catch (error) {
                    // If the fetch fails, skip preselecting the vendor; the user can still select one manually
                    console.error("Failed to fetch the carried-over vendor:", error)
                }
            }

            // Fetch the complete default inventory location if configured
            if (window.CURRENT_USER?.service_company?.default_purchase_order_inventory_location) {
                const inventory_location_id = window.CURRENT_USER?.service_company?.default_purchase_order_inventory_location
                const inventoryLocationEndpoint = DjangoUrls["inventory:api-inventory-locations-detail"](window.MARKETPLACE_ENTITY_SLUG, inventory_location_id)

                try {
                    const inventoryLocationResponse = await fetch(inventoryLocationEndpoint)
                    if (inventoryLocationResponse.ok) {
                        const inventoryLocation = await inventoryLocationResponse.json()

                        this.setState((state, props) => {
                            let updatedState = state
                            updatedState.selectedInventoryLocation = inventoryLocation
                            updatedState.purchaseOrderData.inventory_location = inventoryLocation.id
                            updatedState.inventory_location_id = inventoryLocation.id
                            return updatedState
                        })
                    }
                    // If fetch fails, silently skip setting the default (user will need to select manually)
                } catch (error) {
                    // If fetch fails, silently skip setting the default (user will need to select manually)
                    console.error('Failed to fetch default inventory location:', error)
                }
            }
        }

        if (historyHasState(history)) {
            document.querySelector(".page-subtitle").innerHTML = FORM_MODE_SUBTITLES[history.state.mode]
            document.querySelector(".back-button").style.display = FORM_MODE_BACK_BUTTON_DISPLAY[history.state.mode]
            this.setState(history.state)
        }
    }

    // Form helpers

    getEstimateParentLineItems = () => {
        // Build the estimate part lines offered in each part row's "Estimate Line Item"
        // dropdown. The shared line item form renders a single, global list for every row,
        // so per-row "eligible plus this row's own selection" filtering is not possible
        // without changing shared components. Instead pass the union: estimate part lines
        // not already on a purchase order, plus any estimate line currently selected on
        // this purchase order (so an existing selection stays visible and editable).
        if (this.state.estimateData === null || Object.keys(this.state.estimateData).length === 0) {
            return []
        }

        const estimatePartLineItems = (this.state.estimateData.line_items || []).filter(lineItem => (
            lineItem.line_item_type === PriceBookItemTypes.part
        ))
        const selectedEstimateLineItemIDs = (this.state.purchaseOrderData?.parts || [])
            .map(part => part.estimate_line_item)
            .filter(estimateLineItemID => valueIsDefined(estimateLineItemID))

        return estimatePartLineItems.filter(lineItem => {
            const isNotOnAPurchaseOrder = !valueIsDefined(lineItem.purchase_order)
            const isSelectedOnThisPurchaseOrder = selectedEstimateLineItemIDs.some(estimateLineItemID => (
                String(estimateLineItemID) === String(lineItem.id)
            ))
            return isNotOnAPurchaseOrder || isSelectedOnThisPurchaseOrder
        })
    }

    updateFormData = (formName, fieldName, fieldValue) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState[formName][fieldName] = fieldValue
            return updatedState
        })
    }

    switchFormMode = (mode) => {
        document.querySelector(".page-subtitle").innerHTML = FORM_MODE_SUBTITLES[mode]
        document.querySelector(".back-button").style.display = FORM_MODE_BACK_BUTTON_DISPLAY[mode]

        if (SECONDARY_FORM_MODES.includes(mode)) {
            history.replaceState(this.state, "", "")
        }

        this.setState((state, props) => {
            let updatedState = state
            updatedState.mode = mode
            history.pushState(updatedState, "", "?mode=" + mode.toLowerCase().replace(/_/g, "-"));
            return updatedState
        })
    }

    switchToPrimaryForm = () => {
        this.setState((state, props) => {
            let updatedState = state

            // Clear the secondary form data
            updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]] = {}
            updatedState[SUBMITTING_NAMES_BY_MODE[state.mode]] = false
            updatedState.errors[ERROR_NAMES_BY_MODE[state.mode]] = {}

            return updatedState
        })
        this.switchFormMode(this.state.defaultMode)
    }

    switchToSecondaryForm = (newFormMode, data, initialData) => {
        this.setState((state, props) => {
            let updatedState = state
            // Set the scroll state
            updatedState.returnScroll = document.querySelector(".main").scrollTop

            updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = {}

            if (data !== null) {
                // To help with uniqueness check
                data.originalDescription = data.description
                updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = deepcopy()(data)
            }
            else {
                if (LINE_ITEM_FORM_MODES.includes(newFormMode)) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].line_item_type = LINE_ITEM_TYPE_BY_MODE[newFormMode]

                    if (newFormMode === FORM_MODES.ADD_PART) {
                        const inventory_location = state.purchaseOrderData.inventory_location.toString()
                        updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].inventory_location = inventory_location
                        updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].inventory_location_label = window.INVENTORY_LOCATION_CHOICES.find(location => location.value === parseInt(inventory_location)).label
                    }
                }
            }

            if (initialData !== null) {
                Object.assign(updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]], initialData)
            }

            return updatedState
        })

        this.switchFormMode(newFormMode)
    }

    resetpurchaseOrderData = (updatedState, state, purchaseOrderData) => {
        updatedState.purchaseOrderData = purchaseOrderData
        updatedState.purchaseOrderData.is_draft = state.isDraft
    }

    updateVendorSelection = (selectedVendor) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedVendor !== null) {
                updatedState.selectedVendor = selectedVendor

                // Set the selection, set the data
                updatedState.purchaseOrderData.vendor = selectedVendor.id
                updatedState.vendor_id = selectedVendor.id

                this.resetpurchaseOrderData(updatedState, state, state.purchaseOrderData)

                // Auto-set billing intention based on vendor's default_billing_vendor
                if (selectedVendor.default_billing_vendor) {
                    // Vendor has a default billing vendor, auto-select it
                    updatedState.selectedBillingVendor = selectedVendor.default_billing_vendor
                    updatedState.purchaseOrderData.billing_vendor = selectedVendor.default_billing_vendor.id
                    updatedState.showBillingVendorDropdown = true
                } else {
                    // Vendor bills for themselves, clear billing_vendor
                    updatedState.selectedBillingVendor = null
                    updatedState.purchaseOrderData.billing_vendor = null
                    updatedState.showBillingVendorDropdown = false
                }
            }
            else {
                // Vendor was unset. Unset data
                updatedState.selectedVendor = null
            }

            return updatedState
        })
    }

    setShowBillingVendorDropdown = (show) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState.showBillingVendorDropdown = show

            // If hiding dropdown, clear billing vendor selection
            if (!show) {
                updatedState.selectedBillingVendor = null
                updatedState.purchaseOrderData.billing_vendor = null
            }

            return updatedState
        })
    }

    updateBillingVendorSelection = (selectedBillingVendor) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedBillingVendor !== null) {
                updatedState.selectedBillingVendor = selectedBillingVendor
                updatedState.purchaseOrderData.billing_vendor = selectedBillingVendor.id
                updatedState.showBillingVendorDropdown = true

                this.resetpurchaseOrderData(updatedState, state, state.purchaseOrderData)
            }
            else {
                // Billing vendor was unset. Unset data
                updatedState.selectedBillingVendor = null
                updatedState.purchaseOrderData.billing_vendor = null
                updatedState.showBillingVendorDropdown = false
            }

            return updatedState
        })
    }

    updateAttachments = (attachmentUploadData) => {
        this.setState((state, props) => {
            let updatedState = state

            updatedState.attachments.push(...attachmentUploadData)
            updatedState.purchaseOrderData.attachments.push(...attachmentUploadData)

            return updatedState
        })
    }

    removePendingAttachment = (filenameOrId) => {
        this.setState((state, props) => {
            let updatedState = state

            // Remove pending attachments by filename OR existing attachments by ID
            const filterFn = (a) => {
                if (a.isPending && a.upload_filename === filenameOrId) return false
                if (a.id && String(a.id) === String(filenameOrId)) return false
                return true
            }

            updatedState.attachments = updatedState.attachments.filter(filterFn)
            updatedState.purchaseOrderData.attachments = updatedState.purchaseOrderData.attachments.filter(filterFn)

            return updatedState
        })
    }

    setIsUploading = (isUploading) => {
        this.setState({ isUploading })
    }

    updateInventoryLocationSelection = (selectedInventoryLocation) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedInventoryLocation !== null) {
                updatedState.selectedInventoryLocation = selectedInventoryLocation

                // Set the selection, set the data
                updatedState.purchaseOrderData.inventory_location = selectedInventoryLocation.id
                updatedState.inventory_location_id = selectedInventoryLocation.id

                this.resetpurchaseOrderData(updatedState, state, state.purchaseOrderData)
            }
            else {
                // Inventory location was unset. Unset data
                updatedState.selectedInventoryLocation = null
            }

            return updatedState
        })
    }

    // Crud Line Items

    addCurrentLineItem = (lineItemListName) => {
        const {isValid, errors} = validateLineItem(this.state.purchaseOrderData, this.state.lineItemData, false, false, false, true, false, false)

        if (isValid) {
            this.setState((state, props) => {
                let updatedState = state

                let data = state.lineItemData
                data.line_item_type = LINE_ITEM_TYPE_MAP[lineItemListName]

                // Re-use an existing ID for this description to avoid uniqueness constraint issues
                if (data.description in state.deletedLineItemIDs) {
                    data.id = state.deletedLineItemIDs[data.description]
                }
                else {
                    data.id = null
                }

                if (!valueIsDefined(data.price)) {
                    data.price = null
                }

                if (data.line_item_type === PriceBookItemTypes.service) {
                    if (!valueIsDefined(data.unit_type)) {
                        data.unit_type = LineItemUnitTypes.hourly
                    }

                    if (data.unit_type === LineItemUnitTypes.flat_rate) {
                        data.quantity = 1
                    }
                }
                else {
                    delete data.unit_type
                }

                updatedState.purchaseOrderData[lineItemListName].push(data)
                updatedState.errors.lineItem = {}
                return updatedState
            })
            this.switchToPrimaryForm()
        }
        else {
            this.setState((state, props) => {
                let updatedState = state
                updatedState.errors.lineItem = errors
                return updatedState
            })
        }
    }

    lineItemEquals = (existing, updated) => {
        // If the object is new, it won't have an id. Check the description instead
        return existing.id !== null ? existing.id === updated.id : existing.description === updated.originalDescription
    }

    updateCurrentLineItem = (lineItemListName) => {
        const {isValid, errors} = validateLineItem(this.state.purchaseOrderData, this.state.lineItemData, false, false, false, true, false, false)

        if (isValid) {
            this.setState((state, props) => {
                let updatedState = state

                let data = state.lineItemData

                if (!valueIsDefined(data.price)) {
                    data.price = null
                }

                if (data.line_item_type === PriceBookItemTypes.service) {
                    if (!valueIsDefined(data.unit_type)) {
                        data.unit_type = LineItemUnitTypes.hourly
                    }

                    if (data.unit_type === LineItemUnitTypes.flat_rate) {
                        data.quantity = 1
                    }
                }
                else {
                    delete data.unit_type
                }

                const lineItemIndex = updatedState.purchaseOrderData[lineItemListName].findIndex(lineItem => this.lineItemEquals(lineItem, state.lineItemData))
                updatedState.purchaseOrderData[lineItemListName][lineItemIndex] = data
                updatedState.errors.lineItem = {}
                return updatedState
            })
            this.switchToPrimaryForm()
        }
        else {
            this.setState((state, props) => {
                let updatedState = state
                updatedState.errors.lineItem = errors
                return updatedState
            })
        }
    }

    deleteCurrentLineItem = (lineItemListName) => {
        this.setState((state, props) => {
            let updatedState = state

            // Save the ID in case this description is going to be reused
            if (state.lineItemData.id) {
                updatedState.deletedLineItemIDs[state.lineItemData.description] = state.lineItemData.id
            }

            updatedState.purchaseOrderData[lineItemListName] = updatedState.purchaseOrderData[lineItemListName].filter(lineItem => !this.lineItemEquals(lineItem, state.lineItemData))
            updatedState.errors.lineItem = {}
            return updatedState
        })
        this.switchToPrimaryForm()
    }

    // Crud Purchase Order

    previewPurchaseOrder = async => {
        // Create a draft and redirect to the preview page
        const endpoint = DjangoUrls["purchase-orders:api-purchase-orders-list"](window.MARKETPLACE_ENTITY_SLUG)

        const onSuccess = (purchaseOrder) => {
            const successUrl = DjangoUrls["purchase-orders:purchase-orders-detail"](window.MARKETPLACE_ENTITY_SLUG, purchaseOrder.id)
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Purchase Order "${purchaseOrder.custom_id || purchaseOrder.id}" draft saved`,
                path: successUrl,
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Purchase Order draft could not be saved",
            })
        }

        this.CUDPurchaseOrder(endpoint, "POST", onSuccess, onError, true)
    }

    createPurchaseOrder = async (isDraft) => {
        const endpoint = DjangoUrls["purchase-orders:api-purchase-orders-list"](window.MARKETPLACE_ENTITY_SLUG)

        const onSuccess = async (purchaseOrder) => {
            // Create attachment records for pending uploads
            const pendingAttachments = this.state.attachments.filter(a => a.isPending)
            if (pendingAttachments.length > 0) {
                const attachmentsEndpoint = DjangoUrls["purchase-orders:api-purchase-orders-attachments-list"](window.MARKETPLACE_ENTITY_SLUG, purchaseOrder.id)

                let failedAttachmentCount = 0

                for (const attachment of pendingAttachments) {
                    try {
                        const response = await fetch(attachmentsEndpoint, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json',
                                'X-CSRFToken': Cookies.get("csrftoken"),
                            },
                            credentials: 'include',
                            body: JSON.stringify({
                                upload_filename: attachment.upload_filename,
                                upload_mimetype: attachment.upload_mimetype,
                                upload_size: attachment.upload_size,
                                file_path: attachment.filePath,
                                is_public: true,
                                version: 2,
                            }),
                        })
                        if (!response.ok) {
                            failedAttachmentCount++
                            console.error('Failed to create attachment record:', response.status, response.statusText)
                        }
                    } catch (error) {
                        failedAttachmentCount++
                        console.error('Failed to create attachment record:', error)
                    }
                }

                if (failedAttachmentCount > 0) {
                    this.addToastToQueue({
                        type: "error",
                        variant: "default",
                        title: `${failedAttachmentCount} ${pluralize("attachment", failedAttachmentCount)} failed to save`,
                    })
                }
            }

            let successUrl
            if(window.CURRENT_USER?.permissions.purchase_orders_list_permission < PERMISSION_LEVEL.FULL) {
                successUrl = DjangoUrls["dashboard:dashboard"](window.MARKETPLACE_ENTITY_SLUG)
            } else if (isDraft || window.CURRENT_USER?.permissions.purchase_orders_view_permission < PERMISSION_LEVEL.FULL) {
                successUrl = DjangoUrls["purchase-orders:purchase-orders-list"](window.MARKETPLACE_ENTITY_SLUG)
            }
            else {
                const param = window.CURRENT_USER?.permissions.purchase_orders_edit_permission === PERMISSION_LEVEL.FULL ? "mode=send-purchase-order" : ""
                successUrl = DjangoUrls["purchase-orders:purchase-orders-detail"](window.MARKETPLACE_ENTITY_SLUG, purchaseOrder.id) + `?${param}`
            }
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Purchase Order "${purchaseOrder.custom_id || purchaseOrder.id}" ${isDraft ? "draft saved" : "created"}`,
                cta:
                    isDraft && window.CURRENT_USER?.permissions.purchase_orders_view_permission >= PERMISSION_LEVEL.FULL ?
                    {
                        children: "View",
                        destination: DjangoUrls["purchase-orders:purchase-orders-detail"](window.MARKETPLACE_ENTITY_SLUG, purchaseOrder.id)
                    }
                    :
                    undefined,
                path: successUrl.split("?")[0],
                delayRender: true,
            })

            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: `Purchase Order ${isDraft ? "draft could not be saved" : "could not be created"}`,
            })
        }

        this.CUDPurchaseOrder(endpoint, "POST", onSuccess, onError, isDraft)
    }

    // Helper to determine if PO is stocked or paid (status >= 5, excluding cancelled which is 7)
    isStockedOrPaid = () => {
        const purchaseOrder = this.state.purchaseOrderData
        // Status values: pending(0), sent(1), acknowledged(2), in_transit(3), delivered(4), stocked(5), paid(6), cancelled(7)
        return purchaseOrder && purchaseOrder.status >= 5 && purchaseOrder.status !== 7
    }

    updatePurchaseOrder = async (resend=false) => {
        // Use stocked-update endpoint for stocked/paid POs, otherwise use standard update
        const isStocked = this.isStockedOrPaid()
        const endpoint = isStocked
            ? DjangoUrls["purchase-orders:api-purchase-orders-stocked-update"](window.MARKETPLACE_ENTITY_SLUG, this.state.purchaseOrderData.id)
            : DjangoUrls["purchase-orders:api-purchase-orders-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.purchaseOrderData.id)
        const successUrl = DjangoUrls["purchase-orders:purchase-orders-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.purchaseOrderData.id) + `${resend ? "?mode=send-purchase-order" : ""}`
        const onSuccess = (purchaseOrder) => {
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Purchase Order "${purchaseOrder.custom_id || purchaseOrder.id}" updated`,
                path: successUrl.split("?")[0],
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Purchase Order could not be updated",
            })
        }

        this.CUDPurchaseOrder(endpoint, "PUT", onSuccess, onError, false)
    }

    deleteDraftPurchaseOrder = async () => {
        const endpoint = DjangoUrls["purchase-orders:api-purchase-orders-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.purchaseOrderData.id)
        const successUrl = DjangoUrls["purchase-orders:purchase-orders-list"](window.MARKETPLACE_ENTITY_SLUG)
        const onSuccess = () => {
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Purchase Order "${this.state.purchaseOrderData.custom_id || this.state.purchaseOrderData.id}" draft deleted`,
                path: successUrl.split("?")[0],
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }

        document.querySelectorAll("#message_modal_confirm_delete_draft .modal__close .button").forEach(button => button.style.display = "none")
        document.querySelector("#message_modal_confirm_delete_draft .modal__close .spinner-centered").style.display = "block"

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Purchase Order draft could not be deleted",
            })
            const deleteDraftModal = document.querySelector("#message_modal_confirm_delete_draft .modal__close")
            if (deleteDraftModal) {
                deleteDraftModal.innerHTML = '<span class="text-invalid"><strong>An unexpected error occurred.</strong></span>'
            }
        }

        this.CUDPurchaseOrder(endpoint, "DELETE", onSuccess, onError, false)
    }

    CUDPurchaseOrder = async (endpoint, endpointMethod, onSuccess, onError, isDraft) => {
        const dataName = "purchaseOrderData"
        const submittingName = "submittingPurchaseOrder"
        const errorDictName = "purchaseOrder"

        const dataManipulator = (data, state) => {
            // Deepcopy first to avoid mutating state — without this,
            // destructive operations (like deleting attachments) corrupt
            // state and clear UI data when the server returns an error.
            const cleanData = deepcopy()(data)

            cleanData.line_items = [...cleanData.parts]

            // Convert blank Purchase Order ID value to null
            cleanData.custom_id = cleanData.custom_id || null

            // Set a blank label list if there aren't any selected
            cleanData.labels = cleanData.labels || []

            // Avoid falsy for Other (value = 0)
            if (!valueIsDefined(cleanData.preferred_shipping_carrier)) {
                cleanData.preferred_shipping_carrier = null
            }
            if (!valueIsDefined(cleanData.preferred_shipping_method)) {
                cleanData.preferred_shipping_method = null
            }

            if (parseInt(cleanData.preferred_shipping_carrier) !== ShippingCarriers.other) {
                cleanData.preferred_shipping_carrier_other_name = ""
            }

            if (parseInt(cleanData.preferred_shipping_method) !== ShippingMethods.other) {
                cleanData.preferred_shipping_method_other_name = ""
            }

            // For existing objects with file uploads, attachments are managed by the
            // FileUpload component via the attachment API — don't send them in the
            // form payload or the nested serializer will overwrite them.
            // For new objects, filter out pending attachments — they're created after
            // the object exists.
            if (cleanData.id && window.FILE_UPLOADS_ENABLED) {
                delete cleanData.attachments
            } else if (cleanData.attachments) {
                cleanData.attachments = cleanData.attachments.filter(a => !a.isPending && a.id)
            }

            cleanData.is_draft = isDraft  // We don't want this to change the actual object
            return cleanData
        }

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "The fields service_company, custom_id must make a unique set.") {
                errorDict["custom_id"] = "A purchase order with this ID already exists."
            }
            else if (fieldName === "line_items") {
                if (typeof message === "string") {
                    // Non-field error for line_items - display the actual message
                    errorDict["line_items"] = message
                } else {
                    errorDict["line_items"] = "Please correct the line item errors below:"

                    // Apply the nested errors
                    this.setState((state, props) => {
                        let updatedState = state

                        const indexedLineItems = [
                            ...updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]][LINE_ITEM_TYPES.PARTS],
                        ]
                        message.map((lineItemError, index) => indexedLineItems[index].errors = lineItemError)

                        return updatedState
                    })
                }
            }
            else if (fieldName === "labels" && Array.isArray(message)) {
                errorDict["labels"] = "Labels must be fewer than 100 characters."
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, setErrors)
    }

    // Crud Vendor

    createVendor = async () => {
        const dataName = "vendorData"
        const submittingName = "submittingVendor"
        const errorDictName = "vendor"
        const endpoint = DjangoUrls["pricebook:api-vendors-list"](window.MARKETPLACE_ENTITY_SLUG)
        const endpointMethod = "POST"

        const onSuccess = (vendor) => {
            this.updateVendorSelection(vendor)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Vendor "${vendor.name}" created`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Vendor could not be created",
            })
        }

        this.createUpdateVendor(endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName)
    }

    updateVendor = async () => {
        const dataName = "vendorData"
        const submittingName = "submittingVendor"
        const errorDictName = "vendor"
        const endpoint = DjangoUrls["pricebook:api-vendors-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedVendor.id)
        const endpointMethod = "PUT"

        const onSuccess = (vendor) => {
            this.updateVendorSelection(vendor)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Vendor "${vendor.name}" updated`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Vendor could not be updated",
            })
        }

        this.createUpdateVendor(endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName)
    }

    // Crud Billing Vendor

    createBillingVendor = async () => {
        const dataName = "billingVendorData"
        const submittingName = "submittingBillingVendor"
        const errorDictName = "billingVendor"
        const endpoint = DjangoUrls["pricebook:api-vendors-list"](window.MARKETPLACE_ENTITY_SLUG)
        const endpointMethod = "POST"

        const onSuccess = (vendor) => {
            this.updateBillingVendorSelection(vendor)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                size: "md",
                title: `Billing vendor "${vendor.name}" created`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                size: "md",
                title: "Billing vendor could not be created",
            })
        }

        this.createUpdateVendor(endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName)
    }

    updateBillingVendor = async () => {
        const dataName = "billingVendorData"
        const submittingName = "submittingBillingVendor"
        const errorDictName = "billingVendor"
        const endpoint = DjangoUrls["pricebook:api-vendors-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedBillingVendor.id)
        const endpointMethod = "PUT"

        const onSuccess = (vendor) => {
            this.updateBillingVendorSelection(vendor)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                size: "md",
                title: `Billing vendor "${vendor.name}" updated`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                size: "md",
                title: "Billing vendor could not be updated",
            })
        }

        this.createUpdateVendor(endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName)
    }

    createUpdateVendor = async (endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName) => {
        const dataManipulator = (data, state) => {
            data.contacts = data.contacts || []

            return data
        }

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "The fields service_company, name must make a unique set.") {
                errorDict["name"] = "A vendor with this name already exists."
            }
            else if (fieldName === "contacts") {
                errorDict["contacts"] = "Please correct the contact errors below:"

                // Apply the nested errors
                this.setState((state, props) => {
                    let updatedState = state
                    message.map((contactError, index) => updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]].contacts[index].errors = contactError)
                    return updatedState
                })
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, setErrors)
    }

    // Handle Actions

    handleActionRequest = (action) => {
        switch (action) {
            case "PURCHASE_ORDER_PREVIEW":
                this.previewPurchaseOrder()
                break
            case "PURCHASE_ORDER_CREATE":
                this.createPurchaseOrder(false)
                break
            case "PURCHASE_ORDER_CREATE_DRAFT":
                this.createPurchaseOrder(true)
                break
            case "PURCHASE_ORDER_UPDATE":
                this.updatePurchaseOrder(false)
                break
            case "PURCHASE_ORDER_UPDATE_AND_RESEND":
                this.updatePurchaseOrder(true)
                break
            case "PURCHASE_ORDER_DELETE_DRAFT":
                window.deleteDraftPurchaseOrder = this.deleteDraftPurchaseOrder
                document.querySelector("#message_modal_confirm_delete_draft").style.display = ""
                window.MicroModal.show("message_modal_confirm_delete_draft")
                break
            case "PURCHASE_ORDER_CANCEL_EDITS":
                location.assign(DjangoUrls["purchase-orders:purchase-orders-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.purchaseOrderData.id))
                break
            case "VENDOR_CREATE":
                this.createVendor()
                break
            case "VENDOR_UPDATE":
                this.updateVendor()
                break
            case "VENDOR_CANCEL_CREATE":
                this.switchToPrimaryForm()
                break
            case "VENDOR_CANCEL_EDITS":
                this.switchToPrimaryForm()
                break
            case "BILLING_VENDOR_CREATE":
                this.createBillingVendor()
                break
            case "BILLING_VENDOR_UPDATE":
                this.updateBillingVendor()
                break
            case "BILLING_VENDOR_CANCEL_CREATE":
                this.switchToPrimaryForm()
                break
            case "BILLING_VENDOR_CANCEL_EDITS":
                this.switchToPrimaryForm()
                break
            case "ADD_PART":
                this.addCurrentLineItem(LINE_ITEM_TYPES.PARTS)
                break
            case "EDIT_PART":
                this.updateCurrentLineItem(LINE_ITEM_TYPES.PARTS)
                break
            case "DELETE_PART":
                this.deleteCurrentLineItem(LINE_ITEM_TYPES.PARTS)
                break
            default:
                console.error(`No action handler exists for action "${action}".`)
        }
    }

    // Render

    foldDataComplete = () => {
        const dateIssuedValid = Boolean(this.state.purchaseOrderData.date_issued)
        const purchaseOrderIDValid = this.state.showCustomPurchaseOrderIDField === false || (this.state.showCustomPurchaseOrderIDField === true && Boolean(this.state.purchaseOrderData.custom_id))

        return dateIssuedValid && purchaseOrderIDValid
    }

    render() {
        if (this.state.purchaseOrderData === null) {
            return <Spinner centered={true} />
        }
        else {
            if (PRIMARY_FORM_MODES.includes(this.state.mode)) {
                return <PurchaseOrderForm
                    mode={this.state.mode}
                    submitting={this.state.submittingPurchaseOrder}
                    purchaseOrder={this.state.purchaseOrderData}
                    errors={this.state.errors.purchaseOrder}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("purchaseOrderData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToSecondaryForm={this.switchToSecondaryForm}
                    updateVendorSelection={this.updateVendorSelection}
                    updateBillingVendorSelection={this.updateBillingVendorSelection}
                    setShowBillingVendorDropdown={this.setShowBillingVendorDropdown}
                    updateInventoryLocationSelection={this.updateInventoryLocationSelection}
                    selectedVendor={this.state.selectedVendor}
                    selectedBillingVendor={this.state.selectedBillingVendor}
                    showBillingVendorDropdown={this.state.showBillingVendorDropdown}
                    selectedInventoryLocation={this.state.selectedInventoryLocation}
                    formatCurrencyValue={currencyFormatter(this.state.currencyCode, this.state.languageCode)}
                    currencySymbol={getCurrencySymbol(this.state.currencyCode, this.state.languageCode)}
                    showCustomPurchaseOrderIDField={this.state.showCustomPurchaseOrderIDField}
                    foldDataComplete={this.foldDataComplete()}
                    shippingCarrierChoices={this.shippingCarrierChoices}
                    shippingMethodChoices={this.shippingMethodChoices}
                    updateAttachments={this.updateAttachments}
                    removePendingAttachment={this.removePendingAttachment}
                    isUploading={this.state.isUploading}
                    setIsUploading={this.setIsUploading}
                    pendingSessionId={this.state.pendingSessionId}
                    returnScroll={this.state.returnScroll}
                    isStockedOrPaid={this.isStockedOrPaid()}
                    parentLineItems={this.getEstimateParentLineItems()}
                    parentLineItemFieldName="estimate_line_item"
                    parentLineItemLabel="Estimate Line Item"
                    sourceEstimate={this.state.estimateData || {}}
                ></PurchaseOrderForm>
            }
            else if (VENDOR_FORM_MODES.includes(this.state.mode)) {
                return <VendorForm
                    mode={this.state.mode}
                    submitting={this.state[SUBMITTING_NAMES_BY_MODE[this.state.mode]]}
                    vendor={this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]]}
                    errors={this.state.errors[ERROR_NAMES_BY_MODE[this.state.mode]]}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData(FORM_DATA_NAMES_BY_MODE[this.state.mode], fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToSecondaryForm={undefined}
                    returnScroll={0}
                ></VendorForm>
            }
            else if (LINE_ITEM_FORM_MODES.includes(this.state.mode)) {
                return <LineItemForm
                    mode={this.state.mode}
                    submitting={this.state.submittingLineItem}
                    parent={this.state.purchaseOrderData}
                    lineItem={this.state.lineItemData}
                    currencySymbol={getCurrencySymbol(this.state.currencyCode, this.state.languageCode)}
                    errors={this.state.errors.lineItem}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("lineItemData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    returnScroll={0}
                    objectName={"Purchase Order"}
                    preferredTimezone={this.state.preferredTimezone}
                    isPurchaseOrderLineItem={true}
                    isStockedPurchaseOrder={this.isStockedOrPaid()}
                    parentLineItems={this.getEstimateParentLineItems()}
                    parentLineItemFieldName="estimate_line_item"
                    parentLineItemLabel="Estimate Line Item"
                ></LineItemForm>
            }
            else {
                return (
                    <div className="data-panel-container data-panel-container--with-margin">
                        <div className="data-panel" aria-label="Unknown Form Mode">
                            <div className="data-panel__form">
                                <p className="data-panel__form__caption">An unhandled form mode was supplied.</p>
                            </div>
                        </div>
                    </div>
                )
            }
        }
    }
}

export default PurchaseOrderCreateContainer;
